import { type ComponentType, useCallback } from 'react'

import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'

import CustomerHubCore, {
  type SizePassportsProps,
} from '../components/CustomerHubCore'
import { type Role } from '../contexts/CustomerHubContext'
import { buildAreaPath, parseAreaPath } from '../lib/areaUrls'

import type { EnvironmentWithChina } from '../App'
import type { TabId } from '../components/CustomerHubCore'
import type { CustomerHubAnalyticsConfig } from '../lib/analytics'

interface IndexProps {
  env: EnvironmentWithChina
  role?: Role
  context?: string
  customerStatus?: 'activeInCart' | 'waiting' | 'serving' | ''
  accountNumber?: string
  customerProfileEmail?: string
  authToken?: string
  analyticsConfig?: CustomerHubAnalyticsConfig
  SizePassports?: ComponentType<SizePassportsProps>
}

const LEGACY_PARAMS = ['screen', 'orderId']

function withoutLegacyParams(search: string): string {
  const params = new URLSearchParams(search)
  LEGACY_PARAMS.forEach((param) => params.delete(param))
  const query = params.toString()
  return query ? `?${query}` : ''
}

export default function Index({
  env,
  role = 'style-advisor',
  context,
  customerStatus,
  accountNumber = 'SF003022921', // Default for demo/development
  customerProfileEmail,
  authToken,
  analyticsConfig,
  SizePassports,
}: IndexProps): JSX.Element {
  const { area, orderId } = useParams()
  const { search } = useLocation()
  const navigate = useNavigate()
  const parsed = parseAreaPath(area, orderId)

  const handleTabChange = useCallback(
    (tab: TabId) =>
      navigate({
        pathname: buildAreaPath(tab),
        search: withoutLegacyParams(search),
      }),
    [navigate, search],
  )
  const handleSelectedOrderIdChange = useCallback(
    (nextOrderId: string | null) =>
      navigate({
        pathname: buildAreaPath('orders', nextOrderId),
        search: withoutLegacyParams(search),
      }),
    [navigate, search],
  )

  if (!parsed) return <Navigate to={{ pathname: '/', search }} replace />

  return (
    <CustomerHubCore
      accountNumber={accountNumber}
      customerProfileEmail={customerProfileEmail}
      env={env}
      customerStatus={customerStatus}
      role={role}
      context={context}
      authToken={authToken}
      activeTab={parsed.tab}
      onTabChange={handleTabChange}
      selectedOrderId={area ? parsed.orderId : undefined}
      onSelectedOrderIdChange={handleSelectedOrderIdChange}
      analyticsConfig={analyticsConfig}
      SizePassports={SizePassports}
    />
  )
}
